import React from 'react';
import { AdsButton } from './ads';
import {
  persistLastCrash,
  formatCrashDebugText,
  getLastCrash
} from '../infrastructure/telemetry/crashDebug';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, copied: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    try {
      persistLastCrash(error, {
        componentStack: errorInfo?.componentStack,
        context: this.props.routeLabel || this.props.title || 'ErrorBoundary'
      });
    } catch {
      /* ignore */
    }
    if (typeof this.props.onError === 'function') {
      this.props.onError(error, errorInfo);
    } else {
      console.error('[ErrorBoundary]', error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, copied: false });
    if (typeof this.props.onRetry === 'function') {
      this.props.onRetry();
    }
  };

  handleCopy = async () => {
    const text =
      formatCrashDebugText() ||
      `${this.state.error?.name || 'Error'}: ${this.state.error?.message || ''}\n${this.state.error?.stack || ''}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.setState({ copied: true });
    } catch (err) {
      console.warn('[ErrorBoundary] copy failed', err);
    }
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const title = this.props.title || 'Une erreur est survenue';
    const message =
      this.props.message ||
      "L'application a rencontré un problème. Vous pouvez réessayer ou recharger la page.";

    const err = this.state.error;
    const last = getLastCrash();
    const detailMessage = err?.message || last?.message || '';
    const detailStack = err?.stack || last?.stack || '';

    return (
      <div className="app-error-boundary" role="alert">
        <div className="app-error-boundary__card">
          <h2>{title}</h2>
          <p>{message}</p>
          {detailMessage ? (
            <pre className="app-error-boundary__details">
              {err?.name ? `${err.name}: ` : ''}
              {detailMessage}
              {detailStack ? `\n\n${detailStack}` : ''}
            </pre>
          ) : (
            <pre className="app-error-boundary__details">
              {formatCrashDebugText() || 'Aucun détail disponible — ouvrez la console (F12).'}
            </pre>
          )}
          <div className="app-error-boundary__actions">
            <AdsButton variant="primary" type="button" onClick={this.handleRetry}>
              {this.props.retryLabel || 'Réessayer'}
            </AdsButton>
            <AdsButton variant="secondary" type="button" onClick={() => void this.handleCopy()}>
              {this.state.copied ? 'Copié' : 'Copier le détail'}
            </AdsButton>
            <AdsButton variant="secondary" type="button" onClick={() => window.location.reload()}>
              {this.props.reloadLabel || 'Recharger'}
            </AdsButton>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
